// src/components/Control/Operator/Geometry.tsx
// «Геометрия робота» (Настройки): схема сверху и сбоку + поля. Это единственный
// источник размеров для робота: fleet_adapter строит из неё tf лидара и камер,
// mission — габарит, запас пути и разворот, vision — угол обзора камеры, laser — где лазерная
// рулетка на поворотном кронштейне камеры 1.
//   robot/<id>/geometry      -> {"geometry", "footprint", "defaults"} (geometry.py на Pi)
//   robot/<id>/geometry/set  <- геометрия целиком | {"reset": true}
// Система координат base_link: начало — середина оси колёс на полу, x вперёд, y влево,
// z вверх. В полях — сантиметры и градусы, на роботе — метры.
import React, { useEffect, useState } from 'react';
import { useRobot } from '../robot/RobotContext';
import { putJson, useZenohJson } from '../../../hooks/useZenohSubscription';

type Cam = { name: string; x: number; y: number; z: number; yaw_deg: number; pitch_deg: number; hfov_deg: number; vfov_deg: number };
export interface RobotGeometry {
  body: { front: number; rear: number; width: number; height: number };
  wheels: { track: number; diameter: number; width: number };
  lidar: { x: number; y: number; z: number; yaw_deg: number; range_min: number; range_max: number };
  cameras: Record<'0' | '1', Cam>;
  bumper: { enabled: boolean; depth: number; width: number; z: number; height: number };
  // x, y, z — где пересекаются оси поворота и наклона; dx, dy, dz — начало луча от них
  // (вперёд / влево / вверх при камере «прямо»); range_offset — добавка к показаниям рулетки
  laser: { x: number; y: number; z: number; dx: number; dy: number; dz: number; range_offset: number };
  margin: number;
}
interface Footprint { front: number; rear: number; half_width: number; radius: number; margin: number }
interface GeometryMsg { geometry: RobotGeometry; footprint: Footprint; defaults: RobotGeometry }

const CAM_COLORS = { '0': '#2463c9', '1': '#d97706' };
const LIDAR_COLOR = '#c62828';
const LASER_COLOR = '#be185d';
const rad = (d: number) => (d * Math.PI) / 180;
const cm = (m: number) => Math.round(m * 1000) / 10;

// То же, что geometry.footprint() на роботе, — чтобы схема менялась сразу при правке
function footprintOf(g: RobotGeometry): Footprint {
  const front = g.body.front + (g.bumper.enabled ? g.bumper.depth : 0);
  const half = Math.max(g.body.width, g.wheels.track + g.wheels.width) / 2;
  return { front, rear: g.body.rear, half_width: half, radius: Math.hypot(Math.max(front, g.body.rear), half), margin: g.margin };
}

// Ось колёс от середины корпуса, м (вперёд +): base_link — середина оси, края корпуса — от неё
const axleOf = (g: RobotGeometry) => (g.body.rear - g.body.front) / 2;
const mm = (m: number) => Math.round(m * 10000) / 10000;

// Сдвигает ось колёс относительно корпуса: длина корпуса та же, края пересчитываются, а датчики,
// стоящие на корпусе, остаются на месте корпуса — их «вперёд от оси» меняется на тот же сдвиг.
function setAxle(d: RobotGeometry, a: number) {
  const len = d.body.front + d.body.rear;
  const lim = Math.min(len / 2 - 0.02, Math.max(-len / 2, a));
  const delta = lim - axleOf(d);
  d.body.front = mm(len / 2 - lim);
  d.body.rear = mm(len / 2 + lim);
  d.lidar.x = mm(d.lidar.x - delta);
  d.cameras['0'].x = mm(d.cameras['0'].x - delta);
  d.cameras['1'].x = mm(d.cameras['1'].x - delta);
  d.laser.x = mm(d.laser.x - delta);
}

// Где нижний край кадра камеры касается пола (м от камеры по горизонтали), или null — пол не виден
function floorFrom(c: Cam): number | null {
  const down = -(c.pitch_deg - c.vfov_deg / 2);   // угол нижнего луча под горизонтом
  if (down <= 0.5) return null;
  return c.z / Math.tan(rad(down));
}

// ---------- схема ----------

const TopView: React.FC<{ g: RobotGeometry; fp: Footprint }> = ({ g, fp }) => {
  const reach = 0.3;  // длина «лучей» обзора камер на схеме, м
  const size = 300;
  const cams = [g.cameras['0'], g.cameras['1']];
  const ymax = Math.max(fp.radius + fp.margin, fp.half_width + 0.08, ...cams.map(c => Math.abs(c.y) + 0.05)) + 0.02;
  const xmax = Math.max(fp.front, ...cams.map(c => c.x)) + reach * 0.85;
  const xmin = -Math.max(fp.radius + fp.margin, fp.rear + 0.05) - 0.02;
  const s = Math.min(size / (2 * ymax), (size - 20) / (xmax - xmin));
  // экран: вверх = +x (вперёд), влево = +y
  const X = (y: number) => size / 2 - y * s;
  const Y = (x: number) => 20 + (xmax - x) * s;
  const b = g.body;
  const wedge = (c: Cam) => {
    const a0 = rad(c.yaw_deg - c.hfov_deg / 2), a1 = rad(c.yaw_deg + c.hfov_deg / 2);
    const p = (a: number) => `${X(c.y + reach * Math.sin(a))},${Y(c.x + reach * Math.cos(a))}`;
    return `${X(c.y)},${Y(c.x)} ${p(a0)} ${p(a1)}`;
  };
  const lid = g.lidar;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="op-geo-svg" role="img" aria-label="Робот сверху" overflow="hidden">
      <text x={6} y={14} className="op-geo-title">Сверху (вперёд — вверх)</text>
      <circle cx={X(0)} cy={Y(0)} r={(fp.radius + fp.margin) * s} className="op-geo-margin" />
      <circle cx={X(0)} cy={Y(0)} r={fp.radius * s} className="op-geo-swept" />
      {(['0', '1'] as const).map(k => (
        <polygon key={k} points={wedge(g.cameras[k])} fill={CAM_COLORS[k]} fillOpacity={0.08} stroke={CAM_COLORS[k]} strokeOpacity={0.8} />
      ))}
      <rect x={X(b.width / 2)} y={Y(b.front)} width={b.width * s} height={(b.front + b.rear) * s} className="op-geo-body" rx={4} />
      {[1, -1].map(side => (
        <rect key={side} x={X(side * g.wheels.track / 2 + g.wheels.width / 2)} y={Y(g.wheels.diameter / 2)}
              width={g.wheels.width * s} height={g.wheels.diameter * s} className="op-geo-wheel" rx={2} />
      ))}
      <rect x={X(g.bumper.width / 2)} y={Y(b.front + Math.max(g.bumper.depth, 0.005))} width={g.bumper.width * s}
            height={Math.max(g.bumper.depth, 0.005) * s} className={`op-geo-bumper ${g.bumper.enabled ? 'on' : ''}`} />
      <circle cx={X(lid.y)} cy={Y(lid.x)} r={0.035 * s} fill={LIDAR_COLOR} fillOpacity={0.25} stroke={LIDAR_COLOR} />
      <line x1={X(lid.y)} y1={Y(lid.x)} x2={X(lid.y + 0.07 * Math.sin(rad(lid.yaw_deg)))} y2={Y(lid.x + 0.07 * Math.cos(rad(lid.yaw_deg)))}
            stroke={LIDAR_COLOR} strokeWidth={2} />
      {(['0', '1'] as const).map(k => {
        const c = g.cameras[k];
        return <circle key={k} cx={X(c.y)} cy={Y(c.x)} r={4} fill={CAM_COLORS[k]} />;
      })}
      {g.laser && (
        <rect x={X(g.laser.y + g.laser.dy) - 3} y={Y(g.laser.x + g.laser.dx) - 3} width={6} height={6} fill={LASER_COLOR} />
      )}
      <circle cx={X(0)} cy={Y(0)} r={3} className="op-geo-origin" />
      <text x={X(0) + 6} y={Y(0) + 4} className="op-geo-note">ось</text>
      <text x={size - 6} y={size - 8} textAnchor="end" className="op-geo-note">
        {cm(fp.front + fp.rear)}×{cm(2 * fp.half_width)} см · разворот ⌀{cm(2 * fp.radius)}
      </text>
    </svg>
  );
};

const SideView: React.FC<{ g: RobotGeometry; fp: Footprint }> = ({ g, fp }) => {
  const w = 300, h = 200;
  const reach = 0.6;
  const xmin = -fp.rear - 0.08, xmax = Math.max(fp.front + reach * 0.7, 0.5);
  const zmax = Math.max(g.body.height, g.lidar.z, g.cameras['0'].z, g.cameras['1'].z) + 0.15;
  const s = Math.min((w - 20) / (xmax - xmin), (h - 30) / zmax);
  const X = (x: number) => 10 + (x - xmin) * s;
  const Z = (z: number) => h - 14 - z * s;
  const b = g.body;
  const bodyBottom = g.wheels.diameter * 0.35;
  const ray = (c: Cam, deg: number) => {
    // луч до пола или на reach
    const a = rad(deg);
    let len = reach;
    if (a < 0) len = Math.min(reach, c.z / Math.sin(-a));
    return `${X(c.x + len * Math.cos(a))},${Z(c.z + len * Math.sin(a))}`;
  };
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="op-geo-svg" role="img" aria-label="Робот сбоку">
      <text x={6} y={14} className="op-geo-title">Сбоку (вперёд — вправо)</text>
      <line x1={0} y1={Z(0)} x2={w} y2={Z(0)} className="op-geo-ground" />
      <line x1={X(xmin)} y1={Z(g.lidar.z)} x2={w} y2={Z(g.lidar.z)} stroke={LIDAR_COLOR} strokeDasharray="4 3" strokeOpacity={0.7} />
      <text x={w - 4} y={Z(g.lidar.z) - 3} textAnchor="end" className="op-geo-note" fill={LIDAR_COLOR}>плоскость лидара</text>
      {(['0', '1'] as const).map(k => {
        const c = g.cameras[k];
        return (
          <polygon key={k} points={`${X(c.x)},${Z(c.z)} ${ray(c, c.pitch_deg + c.vfov_deg / 2)} ${ray(c, c.pitch_deg - c.vfov_deg / 2)}`}
                   fill={CAM_COLORS[k]} fillOpacity={0.12} stroke={CAM_COLORS[k]} strokeOpacity={0.6} />
        );
      })}
      <rect x={X(-b.rear)} y={Z(b.height)} width={(b.front + b.rear) * s} height={(b.height - bodyBottom) * s} className="op-geo-body" rx={4} />
      <circle cx={X(0)} cy={Z(g.wheels.diameter / 2)} r={(g.wheels.diameter / 2) * s} className="op-geo-wheel" />
      <rect x={X(b.front)} y={Z(g.bumper.z + g.bumper.height)} width={Math.max(g.bumper.depth, 0.005) * s} height={g.bumper.height * s}
            className={`op-geo-bumper ${g.bumper.enabled ? 'on' : ''}`} />
      <rect x={X(g.lidar.x) - 0.035 * s} y={Z(g.lidar.z) - 0.02 * s} width={0.07 * s} height={0.03 * s} fill={LIDAR_COLOR} fillOpacity={0.4} stroke={LIDAR_COLOR} />
      {(['0', '1'] as const).map(k => (
        <circle key={k} cx={X(g.cameras[k].x)} cy={Z(g.cameras[k].z)} r={4} fill={CAM_COLORS[k]} />
      ))}
      {g.laser && (
        <>
          <line x1={X(g.laser.x + g.laser.dx)} y1={Z(g.laser.z + g.laser.dz)} x2={X(g.laser.x + g.laser.dx + 0.12)} y2={Z(g.laser.z + g.laser.dz)}
                stroke={LASER_COLOR} strokeWidth={1.5} strokeDasharray="3 2" />
          <rect x={X(g.laser.x + g.laser.dx) - 3} y={Z(g.laser.z + g.laser.dz) - 3} width={6} height={6} fill={LASER_COLOR} />
        </>
      )}
      <circle cx={X(0)} cy={Z(0)} r={3} className="op-geo-origin" />
    </svg>
  );
};

// ---------- поля ----------

const Field: React.FC<{ label: string; value: number; unit: 'см' | '°'; min: number; max: number; onChange: (v: number) => void; title?: string }> = ({ label, value, unit, min, max, onChange, title }) => {
  const shown = unit === 'см' ? cm(value) : Math.round(value * 10) / 10;
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const n = Number(draft.replace(',', '.'));
    if (Number.isFinite(n)) {
      const lim = Math.min(max, Math.max(min, n));
      onChange(unit === 'см' ? lim / 100 : lim);
    }
    setDraft(null);
  };
  return (
    <label className="op-num" title={title}>
      <span>{label}, {unit}</span>
      <input type="number" step={unit === 'см' ? 0.5 : 1} min={min} max={max} value={draft ?? shown}
             onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') commit(); }} />
    </label>
  );
};

const GeometryPanel: React.FC = () => {
  const { session, robotId, notify } = useRobot();
  const [msg, setMsg] = useState<GeometryMsg | null>(null);
  const [draft, setDraft] = useState<RobotGeometry | null>(null);
  useZenohJson<GeometryMsg>(session, `robot/${robotId}/geometry`, m => setMsg(m));
  useEffect(() => setDraft(null), [robotId]);

  if (!msg) {
    return (
      <div className="feature-card">
        <h3><span>Геометрия робота</span><span className="badge off">нет данных</span></h3>
        <p className="hint">Робот не прислал геометрию (старая версия ПО на Pi или нет связи).</p>
      </div>
    );
  }
  const g = draft ?? msg.geometry;
  const fp = footprintOf(g);
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(msg.geometry);
  const lidarDirty = dirty && JSON.stringify(draft!.lidar) !== JSON.stringify(msg.geometry.lidar);
  const edit = (fn: (d: RobotGeometry) => void) => {
    const d: RobotGeometry = JSON.parse(JSON.stringify(g));
    fn(d);
    setDraft(d);
  };
  const save = async () => {
    const ok = await putJson(session, `robot/${robotId}/geometry/set`, draft);
    notify(ok ? 'Геометрия сохранена на роботе' + (lidarDirty ? '. Лидар: перезапустите стек на роботе, чтобы SLAM учёл новое положение' : '')
      : 'Не удалось отправить геометрию', ok ? 'success' : 'error');
    if (ok) setDraft(null);
  };
  const reset = async () => {
    if (!window.confirm('Вернуть все размеры к значениям по умолчанию?')) return;
    const ok = await putJson(session, `robot/${robotId}/geometry/set`, { reset: true });
    if (ok) setDraft(null);
  };

  const camFields = (k: '0' | '1') => {
    const c = g.cameras[k];
    const floor = floorFrom(c);
    return (
      <fieldset key={k} className="op-geo-group">
        <legend><span className="op-geo-dot" style={{ background: CAM_COLORS[k] }} /> {c.name}</legend>
        <div className="op-num-grid">
          <Field label="Вперёд от оси" unit="см" value={c.x} min={-50} max={50} onChange={v => edit(d => { d.cameras[k].x = v; })} />
          <Field label="Влево от центра" unit="см" value={c.y} min={-50} max={50} onChange={v => edit(d => { d.cameras[k].y = v; })} />
          <Field label="Высота над полом" unit="см" value={c.z} min={0} max={150} onChange={v => edit(d => { d.cameras[k].z = v; })} />
          <Field label="Поворот влево" unit="°" value={c.yaw_deg} min={-180} max={180} onChange={v => edit(d => { d.cameras[k].yaw_deg = v; })}
                 title="Куда смотрит камера относительно «вперёд» робота; вправо — минус" />
          <Field label="Наклон вверх" unit="°" value={c.pitch_deg} min={-90} max={90} onChange={v => edit(d => { d.cameras[k].pitch_deg = v; })}
                 title="Вниз — минус" />
          <Field label="Обзор по горизонтали" unit="°" value={c.hfov_deg} min={10} max={180} onChange={v => edit(d => { d.cameras[k].hfov_deg = v; })} />
          <Field label="Обзор по вертикали" unit="°" value={c.vfov_deg} min={10} max={180} onChange={v => edit(d => { d.cameras[k].vfov_deg = v; })} />
        </div>
        <p className="hint">{floor === null ? 'Пол в кадр не попадает — камера смотрит слишком высоко.'
          : `Пол виден с ${cm(floor + c.x - fp.front)} см перед корпусом.`}</p>
      </fieldset>
    );
  };

  return (
    <>
      <div className="feature-card op-geo">
        <h3><span>Геометрия робота</span>{dirty && <span className="badge warn">не сохранено</span>}</h3>
        <div className="op-geo-views">
          <TopView g={g} fp={fp} />
          <SideView g={g} fp={fp} />
        </div>
        <ul className="op-geo-facts">
          <li>Габарит: {cm(fp.front + fp.rear)} × {cm(2 * fp.half_width)} см, круг разворота на месте ⌀ {cm(2 * fp.radius)} см
            (пунктир); запас пути по карте — {cm(fp.radius + fp.margin)} см от центра оси.</li>
          <li>Лидар видит только на своей высоте ({cm(g.lidar.z)} см): то, что ниже, — для него пусто, такие препятствия ловят
            камера 2 и (в будущем) бампер.</li>
          <li>Начало координат — середина оси колёс на полу; «вперёд» — +x, «влево» — +y.</li>
        </ul>
        <div className="row">
          <button type="button" className="op-btn primary" disabled={!dirty} onClick={save}>Сохранить на роботе</button>
          <button type="button" className="op-btn" disabled={!dirty} onClick={() => setDraft(null)}>Отменить правки</button>
          <button type="button" className="op-btn" onClick={reset}>По умолчанию</button>
        </div>
        {lidarDirty && <p className="hint">Положение лидара SLAM читает при запуске: после сохранения перезапустите стек на роботе.</p>}
      </div>

      <div className="feature-card">
        <fieldset className="op-geo-group">
          <legend>Корпус</legend>
          <div className="op-num-grid">
            <Field label="Передний край от оси" unit="см" value={g.body.front} min={2} max={100} onChange={v => edit(d => { d.body.front = v; })} />
            <Field label="Задний край от оси" unit="см" value={g.body.rear} min={0} max={100} onChange={v => edit(d => { d.body.rear = v; })} />
            <Field label="Ширина" unit="см" value={g.body.width} min={5} max={100} onChange={v => edit(d => { d.body.width = v; })} />
            <Field label="Высота" unit="см" value={g.body.height} min={2} max={150} onChange={v => edit(d => { d.body.height = v; })} />
            <Field label="Запас вокруг" unit="см" value={g.margin} min={0} max={30} onChange={v => edit(d => { d.margin = v; })}
                   title="Добавляется к габариту при планировании пути и остановке" />
          </div>
          <p className="hint">Мерьте по самым выступающим частям: кронштейны, камера, провода.</p>
        </fieldset>
        <fieldset className="op-geo-group">
          <legend>Колёса</legend>
          <div className="op-num-grid">
            <Field label="Колея (между центрами)" unit="см" value={g.wheels.track} min={5} max={100} onChange={v => edit(d => { d.wheels.track = v; })} />
            <Field label="Диаметр" unit="см" value={g.wheels.diameter} min={1} max={50} onChange={v => edit(d => { d.wheels.diameter = v; })} />
            <Field label="Ширина колеса" unit="см" value={g.wheels.width} min={0.5} max={20} onChange={v => edit(d => { d.wheels.width = v; })} />
            <Field label="Ось от середины корпуса, вперёд" unit="см" value={axleOf(g)} min={-50} max={50}
                   onChange={v => edit(d => setAxle(d, v))}
                   title="Назад — минус. Робот разворачивается на месте вокруг середины оси колёс" />
          </div>
          <p className="hint">Ось колёс — центр разворота на месте и начало координат. Вынос оси вперёд или назад от середины
            корпуса меняет края корпуса «от оси» (длина {cm(g.body.front + g.body.rear)} см та же) и сдвигает на столько же
            положение лидара, камер и рулетки «вперёд от оси» — они стоят на корпусе. Для схемы и габарита; одометрия
            калибруется отдельно (odom_* в config/robot.yaml).</p>
        </fieldset>
        <fieldset className="op-geo-group">
          <legend><span className="op-geo-dot" style={{ background: LIDAR_COLOR }} /> Лидар</legend>
          <div className="op-num-grid">
            <Field label="Вперёд от оси" unit="см" value={g.lidar.x} min={-50} max={50} onChange={v => edit(d => { d.lidar.x = v; })} />
            <Field label="Влево от центра" unit="см" value={g.lidar.y} min={-50} max={50} onChange={v => edit(d => { d.lidar.y = v; })} />
            <Field label="Высота луча" unit="см" value={g.lidar.z} min={0} max={150} onChange={v => edit(d => { d.lidar.z = v; })} />
            <Field label="Куда смотрит его 0°" unit="°" value={g.lidar.yaw_deg} min={-180} max={180} onChange={v => edit(d => { d.lidar.yaw_deg = v; })}
                   title="Поворот нуля лидара относительно «вперёд» робота, влево — плюс" />
            <Field label="Не видеть ближе" unit="см" value={g.lidar.range_min} min={0} max={100} onChange={v => edit(d => { d.lidar.range_min = v; })}
                   title="Отражения ближе — от самого робота, они отбрасываются" />
          </div>
        </fieldset>
        {camFields('0')}
        {g.laser && (
          <fieldset className="op-geo-group">
            <legend><span className="op-geo-dot" style={{ background: LASER_COLOR }} /> Лазерная рулетка (на поворотной камере 1)</legend>
            <div className="op-num-grid">
              <Field label="Оси кронштейна: вперёд от оси колёс" unit="см" value={g.laser.x} min={-50} max={50}
                     onChange={v => edit(d => { d.laser.x = v; })} title="Где пересекаются оси поворота и наклона" />
              <Field label="Оси кронштейна: влево" unit="см" value={g.laser.y} min={-50} max={50} onChange={v => edit(d => { d.laser.y = v; })} />
              <Field label="Оси кронштейна: высота над полом" unit="см" value={g.laser.z} min={0} max={150} onChange={v => edit(d => { d.laser.z = v; })} />
              <Field label="Луч от осей: вперёд" unit="см" value={g.laser.dx} min={-20} max={20} onChange={v => edit(d => { d.laser.dx = v; })}
                     title="При камере «прямо»: где начинается луч (ноль рулетки) относительно осей" />
              <Field label="Луч от осей: влево" unit="см" value={g.laser.dy} min={-20} max={20} onChange={v => edit(d => { d.laser.dy = v; })} />
              <Field label="Луч от осей: вверх" unit="см" value={g.laser.dz} min={-20} max={20} onChange={v => edit(d => { d.laser.dz = v; })} />
              <Field label="Добавка к показаниям" unit="см" value={g.laser.range_offset} min={-20} max={20}
                     onChange={v => edit(d => { d.laser.range_offset = v; })}
                     title="Если рулетка меряет не от точки начала луча: например, от задней грани модуля — минус его длина" />
            </div>
            <p className="hint">Неточность этих размеров на сантиметр почти не сказывается: обмер стены рулеткой калибрует
              серво вместе с ними. Луч должен идти вдоль «вперёд» кронштейна.</p>
          </fieldset>
        )}
        {camFields('1')}
        <fieldset className="op-geo-group">
          <legend>Бампер <span className="badge off">на будущее</span></legend>
          <label className="op-check">
            <input type="checkbox" checked={g.bumper.enabled} onChange={e => edit(d => { d.bumper.enabled = e.target.checked; })} />
            Установлен (учитывать в габарите)
          </label>
          <div className="op-num-grid">
            <Field label="Выступ вперёд" unit="см" value={g.bumper.depth} min={0} max={20} onChange={v => edit(d => { d.bumper.depth = v; })} />
            <Field label="Ширина" unit="см" value={g.bumper.width} min={2} max={100} onChange={v => edit(d => { d.bumper.width = v; })} />
            <Field label="Низ над полом" unit="см" value={g.bumper.z} min={0} max={50} onChange={v => edit(d => { d.bumper.z = v; })} />
            <Field label="Высота" unit="см" value={g.bumper.height} min={0.5} max={50} onChange={v => edit(d => { d.bumper.height = v; })} />
          </div>
          <p className="hint">Датчиков бампера пока нет: он только рисуется и (если отмечен) удлиняет габарит.</p>
        </fieldset>
      </div>
    </>
  );
};

export default GeometryPanel;
